"use client";

import { ArrowRight, Calculator, Wallet, BookOpen, ChartLine as LineChart, Compass, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheFundingSpectrum() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    number: "01",
    title: "Fix-and-Flip",
    description: "High-leverage capital designed for rapid acquisition and renovation cycles, ensuring you never miss a deal.",
    Icon: Calculator
  }, {
    number: "02",
    title: "BRRRR Strategy",
    description: "Comprehensive funding for the Buy, Rehab, Rent, Refinance, Repeat model to scale your portfolio efficiently.",
    Icon: Wallet
  }, {
    number: "03",
    title: "Buy-and-Hold",
    description: "Long-term debt solutions for rental properties, providing stability and cash flow optimization for investors.",
    Icon: BookOpen
  }, {
    number: "04",
    title: "New Construction",
    description: "Capital for modular and ground-up builds, covering the full lifecycle from site prep to final certificate.",
    Icon: LineChart
  }, {
    number: "05",
    title: "Gap Equity",
    description: "Second-position financing that bridges the down payment gap, allowing you to close with minimal cash out-of-pocket.",
    Icon: Compass
  }, {
    number: "06",
    title: "First Mortgage",
    description: "Professional-grade primary debt structures tailored for experienced real estate investors and developers.",
    Icon: ShieldCheck
  }];
  return <section data-section-id="241" id="the-funding-spectrum" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono italic text-base text-muted-foreground/70">01</span>
              <span className="h-px w-8 bg-border" />
              <span className="font-mono uppercase tracking-widest text-xs text-muted-foreground">
                Funding Engines
              </span>
            </div>
            <h2 className="font-default font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tighter text-foreground">
              Architecting capital for the{" "}
              <span className="italic font-light text-primary">modern investor.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-md lg:ml-auto">
              We provide the infrastructure to scale your real estate business. From first mortgages to gap equity, our engines are built to ensure you close deals with institutional speed and precision.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/60">
          {services.map((service, i) => {
          const Icon = service.Icon;
          return <div key={i} data-index={i} className={`group relative bg-card p-8 md:p-10 border border-transparent hover:border-primary transition-all duration-500 ease-out hover:-translate-y-0.5 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: gridVisible ? `${i * 80}ms` : "0ms"
          }}>
                {}
                <span className="absolute top-6 right-6 font-mono italic text-3xl text-muted-foreground/30 group-hover:text-primary transition-colors duration-500">
                  {service.number}
                </span>

                {}
                <div className="mb-10">
                  <Icon className="w-10 h-10 text-primary" strokeWidth={1.25} aria-hidden="true" />
                </div>

                {}
                <h3 className="font-default font-semibold text-2xl text-foreground mb-3 leading-snug">
                  {service.title}
                </h3>

                {}
                <p className="text-sm md:text-base leading-relaxed text-muted-foreground mb-8 max-w-sm">
                  {service.description}
                </p>

                {}
                <Link to="/CapitalSolutionsPage" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-foreground hover:text-primary transition-colors duration-300">
                  <span>View details</span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>;
        })}
        </div>

        {}
        <div ref={ctaRef} className={`mt-16 md:mt-20 text-center transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button asChild variant="link" className="text-foreground hover:text-primary font-mono uppercase tracking-wider text-sm h-auto p-0">
            <Link to="/PreQualificationFormPage" className="inline-flex items-center gap-2 group">
              <span className="border-b border-border group-hover:border-primary pb-1 transition-colors duration-300">
                Get pre-qualified today
              </span>
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}